import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/shared/api/api-client';
import type {
  Practice,
  PracticeSetupDetail,
} from '@/types/settings-api';

/**
 * Full Practice record for the Edit dialog (PROD-758).
 *
 * `GET /api/v1/Practice/Details/{practiceSetupId}`
 *
 * Returns `Practice` with nested `practiceSetupDetail`, working hours,
 * exam rooms, and holidays. The dialog only edits the `practiceSetupDetail`
 * subtree; working hours / rooms ship via separate endpoints.
 */
export function usePractice(practiceSetupId: string | null | undefined) {
  return useQuery({
    queryKey: ['settings', 'practice-by-id', practiceSetupId],
    queryFn: ({ signal }) => {
      if (!practiceSetupId) throw new Error('usePractice called without practiceSetupId');
      return get<Practice>(`/api/v1/Practice/Details/${practiceSetupId}`, { signal });
    },
    enabled: !!practiceSetupId,
  });
}

/**
 * Update an existing Practice.
 *
 * `PUT /api/Practice/{practiceSetupId}` — body is just `PracticeSetupDetail`,
 * NOT the full `Practice`. Working hours / rooms / holidays are kept on
 * their own endpoints (Flutter `updatePracticeById` line 817-826).
 *
 * Wire shape verified against dev cURL 2026-05-12:
 * - ISO date without trailing `Z` for `licenseExpirationDate`
 * - lat/lng as strings (`"0.0"` default, NOT numbers)
 * - `billingDetails` may ship as all-empty strings
 * - `licenseAuthority` is a GUID, not a slug
 */
export function useUpdatePractice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (detail: PracticeSetupDetail) =>
      put<unknown>(`/api/Practice/${detail.practiceSetupId}`, detail),
    onSuccess: (_data, detail) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practices-list'] });
      queryClient.invalidateQueries({
        queryKey: ['settings', 'practice-by-id', detail.practiceSetupId],
      });
    },
  });
}

/**
 * Create a new Practice (PROD-758).
 *
 * `POST /api/Practice` — body is the `PracticeSetupDetail` shape with
 * `practiceSetupId: ""`. Backend allocates the GUID and returns it in
 * the response body. Flutter parity: `createPractice` in
 * `settings_data_source_impl.dart:766`.
 *
 * Wire shape verified against dev cURL 2026-05-13 (matches the update
 * shape exactly, just with empty `practiceSetupId` and a 200 OK that
 * contains the new id in the response).
 */
export interface AddPracticeResponse {
  practiceSetupId?: string;
  /** Backend may return any of these envelope shapes; consumers read what they need. */
  practiceId?: string;
  id?: string;
  status?: string;
}

export function useCreatePractice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (detail: PracticeSetupDetail) =>
      post<AddPracticeResponse>('/api/Practice', detail),
    onSuccess: (resp) => {
      // TODO: harden once POST response cURL confirmed (@samer review #89).
      //
      // We DON'T have the POST /api/Practice RESPONSE cURL — only the
      // request body. The three keys below (practiceSetupId / practiceId
      // / id) are a best-effort fallback chain. On the first real smoke
      // test against dev, capture the actual response key, drop the
      // chain to just that one key, remove this warn, remove the
      // page-level fallback to /settings/practices, and delete this
      // TODO. Until then the warn fires loudly so the wrong-field
      // assumption surfaces at the FIRST real call rather than silently
      // breaking the post-create navigation.
      const hasKnownId =
        resp?.practiceSetupId || resp?.practiceId || resp?.id;
      if (!hasKnownId) {
        // eslint-disable-next-line no-console
        console.warn(
          '[useCreatePractice] POST /api/Practice succeeded but the response did not contain practiceSetupId, practiceId, or id. ' +
            'Caller will fall back to the practices list rather than navigating to the new record. Response shape:',
          resp,
        );
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'practices-list'] });
    },
  });
}
